import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Flame, Medal, Star, Lock, Gift, Trophy } from "lucide-react";
import { api } from "@/lib/api";

const LEVEL_TONE = {
  bronze: "bg-amber-700",
  prata: "bg-stone-400",
  ouro: "bg-yellow-500",
  chef_master: "bg-emerald-700",
};

const initials = (name) =>
  (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

/** FASE 20 — gamification card: XP, level, streak, badges and monthly ranking. */
export default function GamificationCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/gamification/me").then(({ data }) => setData(data)).catch((err) => console.error(err));
  }, []);

  if (!data?.me) return null;
  const me = data.me;
  const nextXp = me.next_level?.min_xp;
  const progress = nextXp
    ? Math.min(100, Math.round(((me.xp - me.level.min_xp) / (nextXp - me.level.min_xp)) * 100))
    : 100;

  return (
    <Card className="p-5 rounded-xl border-stone-200 bg-stone-950 text-white" data-testid="gamification-card">
      {/* Campeão do mês anterior — parabéns ao vencedor */}
      {data.champion && (
        <div
          className="mb-4 flex items-center gap-3 rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-4 py-2.5"
          data-testid="gamification-champion"
        >
          <Trophy className="h-5 w-5 text-yellow-400 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-bold text-emerald-300">
              🏆 Parabéns! Você foi o campeão de {data.champion.month_label} com{" "}
              {data.champion.month_xp}% de aproveitamento
            </div>
            {data.champion.prize_title && (
              <div className="text-xs text-emerald-200/80 truncate">
                Prêmio conquistado: {data.champion.prize_title}
              </div>
            )}
          </div>
        </div>
      )}
      {/* FASE 21 — monthly prize */}
      {data.prize && (
        <div
          className="mb-4 flex items-center gap-3 rounded-lg border border-yellow-500/40 bg-yellow-500/10 px-4 py-2.5"
          data-testid="gamification-prize"
        >
          <Gift className="h-5 w-5 text-yellow-400 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-bold text-yellow-300">
              Prêmio do mês para o 1º lugar: {data.prize.title}
            </div>
            {data.prize.description && (
              <div className="text-xs text-yellow-200/70 font-body truncate">
                {data.prize.description}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`${LEVEL_TONE[me.level.key] || "bg-stone-500"} text-white border-none font-bold`} data-testid="gamification-level">
              <Medal className="h-3 w-3 mr-1" /> {me.level.name}
            </Badge>
            <span className="font-display text-lg font-bold" data-testid="gamification-xp">{me.xp} XP</span>
            {me.streak > 0 && (
              <span className="inline-flex items-center gap-1 text-orange-400 text-sm font-bold" data-testid="gamification-streak">
                <Flame className="h-4 w-4" /> {me.streak} dia(s) seguidos
              </span>
            )}
          </div>
          {nextXp ? (
            <div className="mt-3">
              <div className="flex justify-between text-[10px] text-stone-400 font-body mb-1">
                <span>{me.level.name}</span>
                <span>{me.next_level.name} ({nextXp} XP)</span>
              </div>
              <div className="h-2 rounded-full bg-stone-800 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          ) : (
            <p className="text-xs text-emerald-400 font-body mt-2">Nível máximo alcançado! 👑</p>
          )}
          <div className="flex gap-2 mt-3 flex-wrap" data-testid="gamification-badges">
            {me.badges.map((b) => (
              <span
                key={b.key}
                title={b.desc}
                className={`inline-flex items-center gap-1 text-[10px] font-body px-2 py-1 rounded-full border ${
                  b.earned
                    ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                    : "border-stone-800 bg-stone-900 text-stone-600"
                }`}
              >
                {b.earned ? <Star className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                {b.name}
              </span>
            ))}
          </div>
        </div>

        <div className="min-w-[190px]">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400/80 font-body mb-2">
            Ranking do mês ({data.month}) · % de aproveitamento — você está em {me.rank}º
          </div>
          <div className="space-y-1.5" data-testid="gamification-ranking">
            {data.top.map((r) => (
              <div key={r.user_id} className={`flex items-center gap-2 rounded-md px-2 py-1 ${r.user_id === me.user_id ? "bg-emerald-500/15" : ""}`}>
                <span className="text-xs font-bold text-stone-400 w-5">{r.rank}º</span>
                <Avatar className="h-6 w-6">
                  <AvatarImage src={r.picture || ""} alt={r.name} />
                  <AvatarFallback className="text-[8px] bg-emerald-700 text-white">{initials(r.name)}</AvatarFallback>
                </Avatar>
                <span className="flex-1 text-xs font-body truncate">{r.name}</span>
                <span className="text-xs font-bold text-emerald-400">{r.month_xp}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
